import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';

interface Item {
  id: number;
  title: string;
}
interface Props {
  heading: string;
  singular: string;
  loadItems: () => Promise<any>;
  createItem: (payload: { title: string }) => Promise<any>;
  updateItem: (id: number, payload: { title: string }) => Promise<any>;
  deleteItem: (id: number) => Promise<any>;
}

const TitleMasterPage: React.FC<Props> = ({ heading, singular, loadItems, createItem, updateItem, deleteItem }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [title, setTitle] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Item | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await loadItems();
      const data = Array.isArray(response) ? response : response?.data;
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError(`Failed to load ${heading.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [heading, loadItems]);

  useEffect(() => {
    load();
  }, [load]);

  const close = () => {
    setShowForm(false);
    setEditingId(null);
    setTitle('');
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = title.trim();
    if (!value) {
      setError(`${singular} title is required.`);
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      if (editingId !== null) await updateItem(editingId, { title: value });
      else await createItem({ title: value });
      setSuccess(`${singular} ${editingId !== null ? 'updated' : 'added'} successfully.`);
      close();
      await load();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      setError(
        String((errors && Object.values(errors).flat()[0]) || err?.response?.data?.message || `Failed to save ${singular.toLowerCase()}.`),
      );
    } finally {
      setSubmitting(false);
    }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingId(deleteTarget.id);
      setError('');
      setSuccess('');
      await deleteItem(deleteTarget.id);
      setDeleteTarget(null);
      setSuccess(`${singular} deleted successfully.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to delete ${singular.toLowerCase()}.`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-full overflow-auto bg-white p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{heading}</h2>
          <p className="mt-1 text-sm text-gray-500">Manage {heading.toLowerCase()} available throughout the system.</p>
        </div>
        <button
          type="button"
          onClick={() => (showForm ? close() : setShowForm(true))}
          className="flex items-center gap-2 rounded-md bg-[#e84545] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? 'Cancel' : `Add ${singular}`}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-5">
          <label className="block text-sm font-medium text-gray-700">
            Title <span className="text-red-500">*</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
              placeholder={`Enter ${singular.toLowerCase()} title`}
            />
          </label>
          <div className="mt-4 flex justify-end">
            <button disabled={submitting} className="rounded-md bg-[#143d7d] px-5 py-2 text-sm font-medium text-white disabled:opacity-60">
              {submitting ? 'Saving...' : editingId !== null ? 'Save Changes' : `Add ${singular}`}
            </button>
          </div>
        </form>
      )}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {loading ? (
              <tr>
                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                  No {heading.toLowerCase()} found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{item.title}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setTitle(item.title);
                          setShowForm(true);
                          setError('');
                          setSuccess('');
                        }}
                        className="rounded p-2 text-blue-700 hover:bg-blue-50"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(item)}
                        disabled={deletingId === item.id}
                        className="rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmModal
        isOpen={!!deleteTarget}
        title={`Delete ${singular}?`}
        message={`Are you sure you want to delete “${deleteTarget?.title || ''}”?`}
        confirmText="Yes, Delete"
        loadingText="Deleting..."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={deletingId !== null}
      />
      <SuccessModal
        isOpen={!!success}
        onClose={() => setSuccess('')}
        title="Success"
        message={success}
        buttonText="OK"
        onButtonClick={() => setSuccess('')}
      />
      <ErrorModal isOpen={!!error} onClose={() => setError('')} title="Operation Failed" message={error} buttonText="OK" />
    </div>
  );
};
export default TitleMasterPage;
